// CSAT + NPS + CEI derivation from the `feedback` table.
// Formulas are documented in docs/csat-cei-metrics.md — keep that in sync with changes here.

export function parseRating(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Response-level sentiment, derived from the survey's Customer Engagement Index answer —
// distinct from groups.sentiment, which reflects general chat tone, not survey outcome.
const CEI_SENTIMENT = { Excellent: "Positive", "Very Good": "Positive", Good: "Neutral", Bad: "Negative" };
export function classifySentiment(ceiRaw) {
  return CEI_SENTIMENT[String(ceiRaw || "").trim()] || null;
}

// CSAT has no independent survey question of its own: the CIA WhatsApp button tap writes
// both `customer_eng_index` (CEI) and a numeric `overall_performance` from the same single
// answer (see wa-group-webhook/handlers/group_events.py — one tap, two derived fields), so
// "Overall Performance Rating" is really CEI recoded, not a separately collected signal.
// CSAT is therefore the average of the two questions that ARE independently collected —
// CEI (how the visit felt) and NPS (would they recommend) — both already on a 0-100 scale.
export function computeCSAT(feedbackRows) {
  const cei = computeCEI(feedbackRows);
  const nps = computeNPS(feedbackRows);
  const pct = cei.score != null && nps.score != null ? Math.round((cei.score + nps.score) / 2) : cei.score ?? nps.score;

  const sentimentCounts = { Positive: 0, Neutral: 0, Negative: 0 };
  feedbackRows.forEach((r) => {
    const s = classifySentiment(r["Customer Engagement Index"]);
    if (s) sentimentCounts[s] += 1;
  });

  return {
    pct,
    total: Math.max(cei.total, nps.total),
    ceiTotal: cei.total,
    npsTotal: nps.total,
    sentimentCounts,
  };
}

// CEI: weighted average of the "Customer Engagement Index" survey answer — one of
// Excellent/Very Good/Good/Bad — evenly spaced across a 0-100 scale (Excellent=100,
// Very Good=66.7, Good=33.3, Bad=0). Replaces the earlier group-signal composite;
// CEI is now derived directly from the survey response, same as CSAT and NPS.
const CEI_LEVELS = ["Excellent", "Very Good", "Good", "Bad"];
const CEI_WEIGHTS = { Excellent: 100, "Very Good": 200 / 3, Good: 100 / 3, Bad: 0 };

export function computeCEI(feedbackRows) {
  const counts = { Excellent: 0, "Very Good": 0, Good: 0, Bad: 0 };
  feedbackRows.forEach((r) => {
    const v = String(r["Customer Engagement Index"] || "").trim();
    if (counts[v] !== undefined) counts[v] += 1;
  });
  const total = CEI_LEVELS.reduce((sum, k) => sum + counts[k], 0);
  const score = total
    ? Math.round(CEI_LEVELS.reduce((sum, k) => sum + counts[k] * CEI_WEIGHTS[k], 0) / total)
    : null;
  return { score, counts, total };
}

// Recommendation Score is a 0-10 scale — 0 is a real, meaningful (strong-detractor) value,
// so unlike parseRating this must not filter out 0. Only non-numeric values (e.g. a raw
// "@question5" placeholder from a broken form-template substitution) come back as null.
export function parseRecommendationScore(v) {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Simplified 2-bucket classification per product decision: 9-10 = Promoter, anything
// below = Dissatisfied. (Standard NPS's middle "Passive" 7-8 band is intentionally not
// used here.)
export function npsClassify(score) {
  if (score == null) return null;
  return score >= 9 ? "Promoter" : "Dissatisfied";
}

// Dashboard-level NPS = ((Promoters - Dissatisfied) / valid responses) * 100, floored at 0
// per product decision (NPS should never display as negative here).
export function computeNPS(feedbackRows) {
  const scores = feedbackRows.map((r) => parseRecommendationScore(r["Recommendation Score (1-10)"])).filter((n) => n != null);
  const total = scores.length;
  if (!total) return { score: null, promoters: 0, dissatisfied: 0, total: 0 };
  const promoters = scores.filter((s) => s >= 9).length;
  const dissatisfied = total - promoters;
  const raw = ((promoters - dissatisfied) / total) * 100;
  return { score: Math.max(0, Math.round(raw)), promoters, dissatisfied, total };
}

const normalizePhone = (v) => String(v || "").replace(/\D/g, "").slice(-10);

// Prefers the real group_row_id FK; falls back to matching by customer number (best-effort —
// the same number can belong to more than one group over time, e.g. a repeat customer).
export function feedbackForGroup(feedbackRows, group) {
  const byFk = feedbackRows.filter((r) => String(r["Group Row Id"]) === String(group._row));
  if (byFk.length) return byFk;
  const num = normalizePhone(group["Customer Number"]);
  if (!num) return [];
  return feedbackRows.filter((r) => normalizePhone(r["Customer Number"]) === num);
}

// A single group's most recent feedback response (if any) — for display, this is shown as
// a Promoter/Dissatisfied classification with the raw score, never as a computed "NPS"
// percentage, since NPS as a statistic isn't meaningful for a single respondent.
export function latestFeedbackForGroup(feedbackRows, group) {
  const matches = feedbackForGroup(feedbackRows, group);
  if (!matches.length) return null;
  return [...matches].sort((a, b) => new Date(b["Created At"] || 0) - new Date(a["Created At"] || 0))[0];
}

export function scoreColor(score) {
  if (score == null) return "#94a3b8"; // slate-400 — unknown
  if (score >= 75) return "#10b981"; // emerald-500 — matches design_guidelines "positive_completed"
  if (score >= 50) return "#f59e0b"; // amber-500 — matches "attention_blocked"
  return "#ef4444"; // red-500 — matches "negative_error"
}
